import { FC, ReactNode } from 'react';
import { Button, Icon, Tooltip } from '@kontentino/ui';
import { faFont } from '@fortawesome/pro-regular-svg-icons/faFont';
import { faPalette } from '@fortawesome/pro-regular-svg-icons/faPalette';
import { faTextSize } from '@fortawesome/pro-regular-svg-icons/faTextSize';
import { useTranslation } from 'react-i18next';

export type QuickActionPrefill = {
  prompt: string;
  selectionStart?: number;
  selectionEnd?: number;
};

type Props = {
  onPrefill: (prefill: QuickActionPrefill) => void;
  fontUploadSlot?: ReactNode;
  isLoading?: boolean;
};

function buildPrefillWithPlaceholder(
  prefix: string,
  placeholder: string,
  suffix: string,
): QuickActionPrefill {
  return {
    prompt: prefix + placeholder + suffix,
    selectionStart: prefix.length,
    selectionEnd: prefix.length + placeholder.length,
  };
}

const ImageEditQuickActions: FC<Props> = ({
  onPrefill,
  fontUploadSlot,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  const actionButtonClass =
    'tw-shrink-0 tw-border-dashed tw-border-purple-100 hover:!tw-bg-purple-10 hover:!tw-text-grayscale-180';
  const iconClass = 'tw-text-purple-100';

  return (
    <>
      <Tooltip
        content={t('imageEdit.changeTextTooltip', {
          defaultValue:
            'Replace the main text in the image. Click, then type your new text — the AI will swap it in while keeping the original font, color, size, and position.',
        })}
        delay={300}
      >
        <Button
          size="small"
          variant="secondary"
          className={actionButtonClass}
          iconBefore={<Icon icon={faFont} className={iconClass} />}
          onClick={() =>
            onPrefill(
              buildPrefillWithPlaceholder(
                'Replace the main text in this image with: "',
                'your new text here',
                '". Keep the exact same font, color, size, and position. Do not change any other text or visual elements.',
              ),
            )
          }
          disabled={isLoading}
          data-cy="image-edit-action-change-text"
        >
          {t('imageEdit.changeText', { defaultValue: 'Change text' })}
        </Button>
      </Tooltip>

      {fontUploadSlot}

      <Tooltip
        content={t('imageEdit.matchBrandColorsTooltip', {
          defaultValue:
            'Recolor text and accent elements to match your brand. Click and replace the example hex codes with your brand colors (e.g., #FF6B00, #003366).',
        })}
        delay={300}
      >
        <Button
          size="small"
          variant="secondary"
          className={actionButtonClass}
          iconBefore={<Icon icon={faPalette} className={iconClass} />}
          onClick={() =>
            onPrefill(
              buildPrefillWithPlaceholder(
                'Recolor the text and accent elements in this image to match these brand colors: ',
                '#000000, #FFFFFF',
                '. Keep all text content, fonts, sizes, positions, and the rest of the design unchanged.',
              ),
            )
          }
          disabled={isLoading}
          data-cy="image-edit-action-match-brand-colors"
        >
          {t('imageEdit.matchBrandColors', {
            defaultValue: 'Match brand colors',
          })}
        </Button>
      </Tooltip>

      <Tooltip
        content={t('imageEdit.fitTextTooltip', {
          defaultValue:
            'Resize text so it fits within its original space. Useful when translations produce longer words that overflow or get cropped.',
        })}
        delay={300}
      >
        <Button
          size="small"
          variant="secondary"
          className={actionButtonClass}
          iconBefore={<Icon icon={faTextSize} className={iconClass} />}
          onClick={() =>
            onPrefill({
              prompt:
                'Adjust the text size and spacing so all text fits within its original bounding box without overflowing or reflowing other elements. Do not change the wording or any other visual elements.',
            })
          }
          disabled={isLoading}
          data-cy="image-edit-action-fit-text"
        >
          {t('imageEdit.fitText', { defaultValue: 'Fit text' })}
        </Button>
      </Tooltip>
    </>
  );
};

export default ImageEditQuickActions;
